import './docPreviewRow.scss';
import {
    useStateIsDocumentLoading,
    useStatePreviewDocument,
    useStatePreviewDocumentWithHighlights,
    useStateQuery,
    useStateResultFor,
    useStateShowAnnotations,
} from '../../../search-hooks';
import React, {useCallback, useEffect} from 'react';
import {PdfViewer} from '../../../../common/pdf-viewer/PdfViewer';
import {TextViewer} from '../../../../common/pdf-viewer/TextViewer';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {ProdDocument, ProductDocument} from '../../../search-types';
import {PreviewDocument} from './docPreviewRow-types';
import {ImageViewer} from '../../../../common/image-viewer/ImageViewer';
import {SearchPageType} from '../../../search-config';
import {doFetchDocumentPreview} from '../../../duck/search-actions';
import {useSpotlightTagTypes} from '../../../../config/config-hooks';
import {ViewerToolbar} from '../../../../common/pdf-viewer/ViewerToolbar';
import {InDocumentSearchProps} from '../../../../common/pdf-viewer/PdfViewer.types';
import {getErrorMessage} from '../../../../utils/utils';

const IMAGE_EXTENSIONS: string[] = ['bmp', 'gif', 'jpeg', 'jpg', 'pbm', 'pcd', 'pct', 'pcx', 'pgm', 'png', 'ppm',
    'psd', 'pxl', 'ras', 'tga', 'tif', 'tiff', 'xbm', 'xpm', 'cdr', 'cgm', 'cmx',
    'dxf', 'emf', 'eps', 'fodg', 'met',];

type DocPreviewRowProps = {
    selectedDocument: ProdDocument | undefined;
};

export const DocPreviewRow = ({selectedDocument}: DocPreviewRowProps): React.JSX.Element | null => {
    const dispatch = useDispatch();
    const searchPageType = SearchPageType.BASIC;
    const query = useStateQuery();
    const resultFor = useStateResultFor();
    const spotlightTagTypes = useSpotlightTagTypes();
    const settingsContainerName = useSelector((state: DefaultRootState) => state.settings.containerName);
    const isDocumentLoading = useStateIsDocumentLoading();
    const previewDocument = useStatePreviewDocument();
    const previewDocumentWithHighlights = useStatePreviewDocumentWithHighlights();
    const showAnnotations = useStateShowAnnotations();

    const [inDocumentSearchProps, setInDocumentSearchProps] = React.useState<InDocumentSearchProps>();

    useEffect(() => {
        if (selectedDocument !== undefined) {
            dispatch(
                doFetchDocumentPreview(
                    selectedDocument,
                    showAnnotations,
                    resultFor,
                    query,
                    settingsContainerName,
                    spotlightTagTypes,
                ),
            );
        }
    }, [dispatch, query, resultFor, searchPageType, selectedDocument, settingsContainerName, showAnnotations, spotlightTagTypes]);

    const isPdfFile = (blob: Blob) => {
        return blob.type === 'application/pdf';
    };

    const isFileExtensionPartOfList = (selectedDocument: ProductDocument, extensionsList: string[]): boolean => {
        const contentType = selectedDocument.metadata?.contentType_string?.split('/').pop();
        if (!contentType) {
            return false;
        }
        return extensionsList.includes(contentType?.toLowerCase());
    };

    const renderPdfViewer = useCallback((): React.JSX.Element => {
        return (
            <PdfViewer
                file={showAnnotations ? previewDocumentWithHighlights?.documentBlob : previewDocument?.documentBlob}
                isLoading={isDocumentLoading || false}
                onInDocumentSearchPropsChange={setInDocumentSearchProps}
            />
        );
    }, [isDocumentLoading, previewDocument?.documentBlob, previewDocumentWithHighlights?.documentBlob, showAnnotations]);

    const renderImageViewer = useCallback(
        (selectedDocument: ProductDocument): React.JSX.Element => {
            return <ImageViewer imageLink={selectedDocument.metadata.previewLink_string}/>;
        },
        [],
    );

    const renderTextViewer = useCallback(
        (selectedDocument: ProductDocument, previewDocument: PreviewDocument | undefined): React.JSX.Element => {
            const titleHtml = previewDocument?.title ? previewDocument.title : selectedDocument.title;
            let contentHtml: string;
            if (previewDocument?.content) {
                contentHtml = previewDocument.content;
            } else {
                contentHtml = selectedDocument?.content
                    ? selectedDocument.content
                    : getErrorMessage(previewDocument?.error);
            }
            return <TextViewer title={titleHtml} content={contentHtml}
                               isLoading={isDocumentLoading || false}
                               onInDocumentSearchPropsChange={setInDocumentSearchProps}/>;
        },
        [isDocumentLoading],
    );

    const renderViewer = useCallback((): React.JSX.Element | null => {
        if (selectedDocument) {
            let renderViewer;
            if (isFileExtensionPartOfList(selectedDocument, IMAGE_EXTENSIONS)) {
                renderViewer = renderImageViewer(selectedDocument);
            } else {
                const blob: Blob | undefined = previewDocument?.documentBlob || previewDocumentWithHighlights?.documentBlob;
                if (blob && isPdfFile(blob)) {
                    renderViewer = renderPdfViewer();
                } else {
                    renderViewer = renderTextViewer(selectedDocument, previewDocument);
                }
            }
            return (
                <div id="preview_content">
                    <ViewerToolbar inDocumentSearchProps={inDocumentSearchProps}/>
                    <div className="details-preview-doc">{renderViewer}</div>
                </div>
            );
        }
        return <></>;
    }, [selectedDocument, inDocumentSearchProps, renderImageViewer, previewDocument, previewDocumentWithHighlights, renderPdfViewer, renderTextViewer]);

    return <>{renderViewer()}</>;
};
